import {useMemo} from 'react'
import {Stack} from '@/components/shared/Stack'
import {useQuery} from '@tanstack/react-query'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {PageLayout} from '@/components/shared/PageLayout'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {CONCEPT_ICON} from '@/lib/concepts'
import {Pagination} from '@/components/ui/pagination'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {AccountControllerAccountsEndpoint} from '@/types/endpoint-contracts'
import {AccountStatusBadge} from '@/types/account'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {routes} from '@/lib/routes'
import {formatDateOnly} from '@/lib/date-utils'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {EntityLink} from '@/components/shared/EntityLink'
import {MutedText} from '@/components/shared/MutedText'

/**
 * Accounts list page — Phase C.1 unified search migration (issue 342.06).
 * Replaces the legacy SearchInput + Status/System/SystemType/Environment
 * Selects with a single {@link UnifiedSearchBar} driven by the
 * {@link AccountControllerAccountsEndpoint} contract.
 *
 * Spec invariants this component upholds:
 *   • Zero implicit default filters — an empty URL shows every account.
 *   • One request per submit / chip removal.
 *   • `q` matches the linked Identity's @Searchable display name through the
 *     1-hop join wired by SearchableContractRegistry. Direct match against
 *     external_identifier (an ExternalId converter-backed field) requires a
 *     path-walker extension — tracked in 342.06.99.
 */
export function Accounts() {
    const services = useServices()
    const i18n = useI18n()
    const {accountStatuses, systemTypes, environmentTypes} = useAppConfig()

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: AccountControllerAccountsEndpoint,
        sortable: true,
        defaultSortBy: AccountControllerAccountsEndpoint.sort.CREATED_AT,
        defaultSortDir: SortDir.DESC,
    })

    const {data: accountsData} = useQuery({
        queryKey: ['accounts', queryString, page, pageSize],
        queryFn: () => services.accounts.list(Object.fromEntries(new URLSearchParams(queryString))),
    })

    const accounts = accountsData?.content || []
    const totalItems = accountsData?.totalElements || 0
    const totalPages = accountsData?.totalPages || 0

    const hasFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    return (
        <PageLayout
            variant="list"
            icon={CONCEPT_ICON.account}
            title={i18n.accounts.allAccounts}
            count={totalItems}
            description={`${i18n.accounts.accountCount({count: totalItems})}${hasFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="accounts-list"
            filters={
                <UnifiedSearchBar
                    contract={AccountControllerAccountsEndpoint}
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.accounts.searchByExternalId}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-identity">{i18n.accounts.identity}</TableHead>
                        <TableHead data-testid="col-target-system">{i18n.accounts.targetSystem}</TableHead>
                        <TableHead data-testid="col-status">{i18n.common.status}</TableHead>
                        <TableHead data-testid="col-created-at">{i18n.common.createdAt}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {accounts.map((account) => (
                        <ClickableTableRow
                            key={account.id}
                            to={routes.identityManagement.accounts.detail(account.id)}
                        >
                            <TableCell className="font-medium text-primary">
                                <EntityLink value={account.identity} testId={`link-account-identity-${account.id}`} stopPropagation/>
                            </TableCell>
                            <TableCell>
                                <Stack>
                                    <EntityLink value={account.targetSystem} testId={`link-account-system-${account.id}`} stopPropagation/>
                                    <MutedText size="xs" data-testid={`account-system-subtitle-${account.id}`}>
                                        {/* eslint-disable-next-line i18next/no-literal-string -- separator character between two i18n-translated labels */}
                                        {`${labelOf(systemTypes, account.targetSystemType)} \u2014 ${labelOf(environmentTypes, account.targetSystem.subType ?? '')}`}
                                    </MutedText>
                                </Stack>
                            </TableCell>
                            <TableCell>
                                {(() => {
                                    const statusBadge = AccountStatusBadge.of(account.status)
                                    const AccIcon = statusBadge?.icon
                                    return (
                                        <Badge variant={statusBadge?.variant ?? BadgeVariant.OUTLINE} className="gap-1">
                                            {AccIcon && <AccIcon className="h-3 w-3" aria-hidden="true"/>}
                                            {labelOf(accountStatuses, account.status)}
                                        </Badge>
                                    )
                                })()}
                            </TableCell>
                            <TableCell>
                                <MutedText>{formatDateOnly(account.createdAt)}</MutedText>
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {accounts.length === 0 && (
                        <TableEmptyState
                            colSpan={4}
                            hasFilters={hasFilters}
                            matchMessage={i18n.accounts.noAccountsMatch}
                            emptyMessage={i18n.accounts.noAccountsFound}
                        />
                    )}
                </TableBody>
            </Table>
        </PageLayout>
    )
}
